import React, { useEffect, useRef, useState } from 'react';

export interface TableSelectOption {
  value: string | number;
  label: string;
}

interface TableSelectProps {
  value: string | number;
  options: (string | number | TableSelectOption)[];
  onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  className?: string;
  disabled?: boolean;
}

const TableSelect: React.FC<TableSelectProps> = ({
  value,
  options,
  onChange,
  className = '',
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const normalizedOptions: TableSelectOption[] = options.map((option) =>
    typeof option === 'object' && option !== null && 'value' in option
      ? option
      : {
          value: option,
          label: String(option),
        }
  );

  const selectedOption = normalizedOptions.find(
    (option) => String(option.value) === String(value)
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () =>
      document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div
      ref={containerRef}
      className={`relative ${className}`}
    >
      <div
        className={`
          flex
          h-10
          items-center
          rounded-xl
          border
          border-stroke
          bg-gray-1
          transition-all
          duration-200

          focus-within:border-primary
          focus-within:ring-2
          focus-within:ring-primary/10

          dark:border-strokedark
          dark:bg-meta-4

          ${disabled ? 'cursor-not-allowed opacity-70' : ''}
        `}
      >
        <div
          className={`
            flex
            flex-1
            items-center
            truncate
            px-3
            text-sm

            ${
              disabled
                ? 'cursor-not-allowed text-bodydark2'
                : 'cursor-pointer text-black dark:text-white'
            }
          `}
          onClick={() => !disabled && setIsOpen((prev) => !prev)}
        >
          {selectedOption?.label}
        </div>

        <span
          className={`
            flex
            items-center
            pr-3
            transition-transform
            duration-200

            ${
              disabled
                ? 'pointer-events-none opacity-50'
                : 'cursor-pointer hover:opacity-80'
            }
          `}
          onClick={(e) => {
            e.stopPropagation();

            if (!disabled) {
              setIsOpen((prev) => !prev);
            }
          }}
        >
          <svg
            className={`
              fill-current
              transition-transform
              duration-300

              ${
                isOpen
                  ? 'rotate-180 text-primary'
                  : 'text-body dark:text-bodydark2'
              }
            `}
            width="20"
            height="20"
            viewBox="0 0 24 24"
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M5.29289 8.29289C5.68342 7.90237 6.31658 7.90237 6.70711 8.29289L12 13.5858L17.2929 8.29289C17.6834 7.90237 18.3166 7.90237 18.7071 8.29289C19.0976 8.68342 19.0976 9.31658 18.7071 9.70711L12.7071 15.7071C12.3166 16.0976 11.6834 16.0976 11.2929 15.7071L5.29289 9.70711C4.90237 9.31658 4.90237 8.68342 5.29289 8.29289Z"
            />
          </svg>
        </span>
      </div>

      {isOpen && (
        <ul
          className="
            absolute
            left-0
            top-full
            z-999999
            mt-1
            max-h-60
            w-full
            overflow-y-auto
            rounded-xl
            border
            border-stroke
            bg-white
            shadow-default
            dark:border-strokedark
            dark:bg-boxdark
          "
        >
          {normalizedOptions.map((option) => (
            <li
              key={option.value}
              onClick={() => {
                const fakeEvent = {
                  target: {
                    value: option.value,
                  },
                } as React.ChangeEvent<HTMLSelectElement>;

                onChange(fakeEvent);

                setIsOpen(false);
              }}
              className={`
                cursor-pointer
                border-b
                border-stroke
                px-3
                py-2
                text-sm
                transition

                hover:bg-primary
                hover:text-white

                dark:border-strokedark
                dark:text-white

                ${
                  String(option.value) === String(value)
                    ? 'bg-primary text-white'
                    : 'text-black dark:text-white'
                }
              `}
            >
              {option.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default TableSelect;